import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import MetricCard from '../components/MetricCard';
import { usePortal } from '../context/PortalContext';
import { clubService } from '../services/clubService';
import {
  ClubFinanceEntry,
  ClubFinanceManualEntryRequest,
  ClubFinancePlayerPosition,
  ClubPaymentIntent,
  ClubPaymentOperationsSummary,
  ClubFinanceSummary,
  ClubWithdrawalRequest,
} from '../types';
import { buildTimestampSuffix, downloadCsv } from '../utils/csv';
import { formatCurrency, formatDateTime, getMemberRoleLabel } from '../utils/formatters';

const SETTLEMENT_METHODS = [
  { value: 'PIX', label: 'PIX' },
  { value: 'CASH', label: 'Dinheiro' },
  { value: 'BANK_TRANSFER', label: 'Transferencia' },
  { value: 'INTERNAL', label: 'Interno' },
  { value: 'OTHER', label: 'Outro' },
] as const;

const getDirectionLabel = (value: ClubFinanceEntry['direction']): string => {
  return value === 'RECEIVABLE' ? 'Jogador deve ao club' : 'Club deve ao jogador';
};

const getStatusLabel = (value: ClubFinanceEntry['status']): string => {
  switch (value) {
    case 'OPEN':
      return 'Em aberto';
    case 'PARTIAL':
      return 'Parcial';
    case 'SETTLED':
      return 'Quitado';
    case 'CANCELLED':
      return 'Cancelado';
    default:
      return value;
  }
};

const getSourceLabel = (value: ClubFinanceEntry['source_type']): string => {
  switch (value) {
    case 'COUNTER_ADJUSTMENT':
      return 'Contador';
    case 'MANUAL':
      return 'Manual';
    case 'EXTERNAL_DEPOSIT':
      return 'Deposito externo';
    case 'EXTERNAL_WITHDRAWAL':
      return 'Saque externo';
    default:
      return value;
  }
};

const getWithdrawalStatusLabel = (value: ClubWithdrawalRequest['status']): string => {
  switch (value) {
    case 'REQUESTED':
      return 'Solicitado';
    case 'UNDER_REVIEW':
      return 'Em revisao';
    case 'RESERVED':
      return 'Reservado';
    case 'PROCESSING':
      return 'Processando';
    case 'PAID':
      return 'Pago';
    case 'FAILED':
      return 'Falhou';
    case 'CANCELLED':
      return 'Cancelado';
    default:
      return value;
  }
};

const getWithdrawalStatusTone = (
  value: ClubWithdrawalRequest['status']
): 'positive' | 'warning' | 'danger' | 'neutral' => {
  switch (value) {
    case 'PAID':
      return 'positive';
    case 'REQUESTED':
    case 'UNDER_REVIEW':
    case 'RESERVED':
    case 'PROCESSING':
      return 'warning';
    case 'FAILED':
    case 'CANCELLED':
      return 'danger';
    default:
      return 'neutral';
  }
};

const formatWithdrawalApproveError = (error: any): string => {
  const rawMessage = String(error?.message || '').trim();
  const status = Number(error?.status || 0);
  const normalizedMessage = rawMessage || 'Erro ao aprovar solicitacao de saque';
  const isAsaasMessage = /Asaas:/i.test(normalizedMessage);

  if ((status === 504 || /timeout/i.test(normalizedMessage)) && isAsaasMessage) {
    return 'Timeout ao comunicar com o Asaas durante a aprovacao. O saque segue em revisao. Tente novamente em instantes e, se persistir, acione a equipe tecnica.';
  }

  if (status === 400 && isAsaasMessage) {
    return `${normalizedMessage}. A transferencia externa foi recusada pelo gateway. Verifique as configuracoes da conta Asaas do club e, se persistir, acione a equipe tecnica.`;
  }

  if (status >= 500) {
    return 'Falha interna ao aprovar saque. Tente novamente em instantes e acione a equipe tecnica se o erro persistir.';
  }

  return normalizedMessage;
};

const getDepositStatusLabel = (value: ClubPaymentIntent['status']): string => {
  switch (value) {
    case 'PENDING':
      return 'Preparando';
    case 'AWAITING_PAYMENT':
      return 'Aguardando pagamento';
    case 'CONFIRMED':
      return 'Confirmado';
    case 'FAILED':
      return 'Falhou';
    case 'EXPIRED':
      return 'Expirado';
    case 'CANCELLED':
      return 'Cancelado';
    default:
      return value;
  }
};

const getDepositStatusTone = (
  value: ClubPaymentIntent['status']
): 'positive' | 'warning' | 'danger' | 'neutral' => {
  switch (value) {
    case 'CONFIRMED':
      return 'positive';
    case 'PENDING':
    case 'AWAITING_PAYMENT':
      return 'warning';
    case 'FAILED':
    case 'EXPIRED':
    case 'CANCELLED':
      return 'danger';
    default:
      return 'neutral';
  }
};

const toFilterTimestamp = (value: string, time: 'start' | 'end'): string | undefined => {
  if (!value) {
    return undefined;
  }

  return time === 'start' ? `${value}T00:00:00` : `${value}T23:59:59`;
};

type EntryStatusFilter = 'PENDING' | 'ALL' | ClubFinanceEntry['status'];
type EntryDirectionFilter = 'ALL' | ClubFinanceEntry['direction'];

const ENTRY_STATUS_OPTIONS: Array<{ value: EntryStatusFilter; label: string }> = [
  { value: 'PENDING', label: 'Pendentes' },
  { value: 'ALL', label: 'Todos' },
  { value: 'OPEN', label: 'Em aberto' },
  { value: 'PARTIAL', label: 'Parcial' },
  { value: 'SETTLED', label: 'Quitado' },
  { value: 'CANCELLED', label: 'Cancelado' },
];

const ENTRY_DIRECTION_OPTIONS: Array<{ value: EntryDirectionFilter; label: string }> = [
  { value: 'ALL', label: 'Tudo' },
  { value: 'RECEIVABLE', label: 'A receber' },
  { value: 'PAYABLE', label: 'A pagar' },
];

const MANUAL_ENTRY_DIRECTION_OPTIONS: Array<{
  value: ClubFinanceManualEntryRequest['direction'];
  label: string;
}> = [
  { value: 'RECEIVABLE', label: 'Jogador deve ao club' },
  { value: 'PAYABLE', label: 'Club deve ao jogador' },
];

const toSlug = (value: string | undefined): string => {
  const normalized = (value || 'club')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalized || 'club';
};

const toSnapshotMoney = (value: unknown, fallback = 0): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const getDepositSnapshot = (
  intent: ClubPaymentIntent
): {
  gross: number;
  net: number;
  fee: number;
  providerStatus: string;
} => {
  const snapshot = intent.metadata_json?.payment_snapshot || {};
  const gross = toSnapshotMoney(snapshot.value, intent.amount_reais);
  const net = toSnapshotMoney(snapshot.net_value, gross);
  const fee = toSnapshotMoney(snapshot.fee, Math.max(gross - net, 0));
  const providerStatus =
    typeof snapshot.status === 'string' && snapshot.status.trim().length > 0
      ? snapshot.status
      : typeof intent.metadata_json?.provider_status === 'string'
        ? intent.metadata_json.provider_status
        : '-';

  return {
    gross,
    net,
    fee,
    providerStatus,
  };
};

const FinanceModal: React.FC<{
  title: string;
  eyebrow?: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, eyebrow, subtitle, onClose, children }) => {
  return (
    <div className="portal-modal-backdrop" role="dialog" aria-modal="true">
      <div className="portal-modal-card">
        <div className="portal-modal-header">
          <div className="portal-modal-title-group">
            {eyebrow ? <p className="portal-section-card__eyebrow">{eyebrow}</p> : null}
            <h3>{title}</h3>
            {subtitle ? <p className="portal-muted">{subtitle}</p> : null}
          </div>
          <button type="button" className="portal-modal-close" onClick={onClose} aria-label="Fechar modal">
            x
          </button>
        </div>
        <div className="portal-modal-body">{children}</div>
      </div>
    </div>
  );
};

const FinancePage: React.FC = () => {
  const { selectedClub } = usePortal();
  const [summary, setSummary] = useState<ClubFinanceSummary | null>(null);
  const [paymentSummary, setPaymentSummary] = useState<ClubPaymentOperationsSummary | null>(null);
  const [depositIntents, setDepositIntents] = useState<ClubPaymentIntent[]>([]);
  const [players, setPlayers] = useState<ClubFinancePlayerPosition[]>([]);
  const [entries, setEntries] = useState<ClubFinanceEntry[]>([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState<ClubWithdrawalRequest[]>([]);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [submittingSettlement, setSubmittingSettlement] = useState(false);
  const [submittingManualEntry, setSubmittingManualEntry] = useState(false);
  const [submittingWithdrawalAction, setSubmittingWithdrawalAction] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedEntryId, setSelectedEntryId] = useState('');
  const [settlementAmount, setSettlementAmount] = useState('');
  const [settlementMethod, setSettlementMethod] = useState<typeof SETTLEMENT_METHODS[number]['value']>('PIX');
  const [settlementNote, setSettlementNote] = useState('');
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [entryStatusFilter, setEntryStatusFilter] = useState<EntryStatusFilter>('PENDING');
  const [entryDirectionFilter, setEntryDirectionFilter] = useState<EntryDirectionFilter>('ALL');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [manualTargetUserId, setManualTargetUserId] = useState('');
  const [manualDirection, setManualDirection] =
    useState<ClubFinanceManualEntryRequest['direction']>('RECEIVABLE');
  const [manualAmount, setManualAmount] = useState('');
  const [manualReason, setManualReason] = useState('');
  const [selectedWithdrawalRequestId, setSelectedWithdrawalRequestId] = useState('');
  const [withdrawalRejectReason, setWithdrawalRejectReason] = useState('');
  const [selectedDepositIntentId, setSelectedDepositIntentId] = useState('');

  const loadOverviewData = useCallback(async () => {
    if (!selectedClub?.id) {
      setSummary(null);
      setPlayers([]);
      setError('');
      return;
    }

    setLoadingOverview(true);

    try {
      const [summaryPayload, paymentAccountPayload, depositPayload, playersPayload, withdrawalPayload] = await Promise.all([
        clubService.getFinanceSummary(selectedClub.id),
        clubService.getPaymentAccount(selectedClub.id),
        clubService.listDepositIntents(selectedClub.id, 40),
        clubService.getFinancePlayers(selectedClub.id),
        clubService.listWithdrawalRequests(selectedClub.id, 40),
      ]);

      setSummary(summaryPayload);
      setPaymentSummary(paymentAccountPayload.summary);
      setDepositIntents(depositPayload);
      setPlayers(playersPayload);
      setWithdrawalRequests(withdrawalPayload);
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar o financeiro do club');
    } finally {
      setLoadingOverview(false);
    }
  }, [selectedClub?.id]);

  useEffect(() => {
    void loadOverviewData();
  }, [loadOverviewData]);

  const loadEntries = useCallback(async () => {
    if (!selectedClub?.id) {
      setEntries([]);
      setError('');
      return;
    }

    if (fromDate && toDate && new Date(fromDate).getTime() > new Date(toDate).getTime()) {
      setEntries([]);
      setSelectedEntryId('');
      setError('A data inicial nao pode ser maior que a data final');
      return;
    }

    setLoadingEntries(true);
    setError('');

    try {
      const entriesPayload = await clubService.getFinanceEntries(selectedClub.id, {
        page: 1,
        limit: 50,
        userId: selectedPlayerId || undefined,
        status:
          entryStatusFilter !== 'ALL' && entryStatusFilter !== 'PENDING'
            ? entryStatusFilter
            : undefined,
        direction: entryDirectionFilter !== 'ALL' ? entryDirectionFilter : undefined,
        from: toFilterTimestamp(fromDate, 'start'),
        to: toFilterTimestamp(toDate, 'end'),
      });

      setEntries(entriesPayload.data);
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar o ledger financeiro');
    } finally {
      setLoadingEntries(false);
    }
  }, [selectedClub?.id, selectedPlayerId, entryStatusFilter, entryDirectionFilter, fromDate, toDate]);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    setSelectedPlayerId('');
    setEntryStatusFilter('PENDING');
    setEntryDirectionFilter('ALL');
    setFromDate('');
    setToDate('');
    setManualTargetUserId('');
    setManualDirection('RECEIVABLE');
    setManualAmount('');
    setManualReason('');
    setDepositIntents([]);
    setPaymentSummary(null);
    setSelectedWithdrawalRequestId('');
    setWithdrawalRejectReason('');
  }, [selectedClub?.id]);

  const filteredEntries = useMemo(() => {
    if (entryStatusFilter === 'PENDING') {
      return entries.filter((entry) => entry.status === 'OPEN' || entry.status === 'PARTIAL');
    }

    return entries;
  }, [entries, entryStatusFilter]);

  const filteredPlayers = useMemo(() => {
    let nextPlayers = selectedPlayerId
      ? players.filter((player) => player.user_id === selectedPlayerId)
      : players;

    if (entryDirectionFilter === 'RECEIVABLE') {
      nextPlayers = nextPlayers.filter((player) => player.open_receivable > 0);
    } else if (entryDirectionFilter === 'PAYABLE') {
      nextPlayers = nextPlayers.filter((player) => player.open_payable > 0);
    }

    return nextPlayers;
  }, [players, selectedPlayerId, entryDirectionFilter]);

  const directionScopeLabel = useMemo(() => {
    if (entryDirectionFilter === 'RECEIVABLE') {
      return 'A receber';
    }

    if (entryDirectionFilter === 'PAYABLE') {
      return 'A pagar';
    }

    return 'Visao geral';
  }, [entryDirectionFilter]);

  const filteredDirectionTotal = useMemo(() => {
    if (entryDirectionFilter === 'RECEIVABLE') {
      return filteredPlayers.reduce((sum, player) => sum + player.open_receivable, 0);
    }

    if (entryDirectionFilter === 'PAYABLE') {
      return filteredPlayers.reduce((sum, player) => sum + player.open_payable, 0);
    }

    return filteredPlayers.reduce((sum, player) => sum + player.net_exposure, 0);
  }, [filteredPlayers, entryDirectionFilter]);

  const totalGatewayFees = useMemo(
    () =>
      (summary?.external_deposit_fee_total || paymentSummary?.deposit_fee_total || 0) +
      (summary?.external_withdrawal_fee_total || paymentSummary?.withdrawal_fee_total || 0),
    [
      paymentSummary?.deposit_fee_total,
      paymentSummary?.withdrawal_fee_total,
      summary?.external_deposit_fee_total,
      summary?.external_withdrawal_fee_total,
    ]
  );

  const playerNameByUserId = useMemo(() => {
    return players.reduce<Record<string, string>>((accumulator, player) => {
      accumulator[player.user_id] = player.user_name;
      return accumulator;
    }, {});
  }, [players]);

  const loading = loadingOverview || loadingEntries;

  const selectedDepositIntent = useMemo(
    () => depositIntents.find((intent) => intent.id === selectedDepositIntentId) || null,
    [depositIntents, selectedDepositIntentId]
  );

  const pendingWithdrawalRequests = useMemo(
    () =>
      withdrawalRequests.filter((request) =>
        ['REQUESTED', 'UNDER_REVIEW', 'RESERVED', 'PROCESSING'].includes(request.status)
      ),
    [withdrawalRequests]
  );

  const withdrawalReviewCount = useMemo(
    () => withdrawalRequests.filter((request) => ['REQUESTED', 'UNDER_REVIEW', 'RESERVED'].includes(request.status)).length,
    [withdrawalRequests]
  );

  const withdrawalProcessingCount = useMemo(
    () => withdrawalRequests.filter((request) => request.status === 'PROCESSING').length,
    [withdrawalRequests]
  );

  const selectedWithdrawalRequest = useMemo(
    () => withdrawalRequests.find((request) => request.id === selectedWithdrawalRequestId) || null,
    [withdrawalRequests, selectedWithdrawalRequestId]
  );

  const selectedEntry = useMemo(
    () => filteredEntries.find((entry) => entry.id === selectedEntryId) || null,
    [filteredEntries, selectedEntryId]
  );

  useEffect(() => {
    if (selectedEntryId && !selectedEntry) {
      setSelectedEntryId('');
      setSettlementAmount('');
      setSettlementMethod('PIX');
      setSettlementNote('');
    }
  }, [selectedEntry, selectedEntryId]);

  useEffect(() => {
    if (!selectedEntry) {
      return;
    }

    setSettlementAmount(String(selectedEntry.outstanding_amount_reais.toFixed(2)));
    setSettlementMethod('PIX');
    setSettlementNote('');
  }, [selectedEntry]);

  useEffect(() => {
    if (selectedWithdrawalRequestId && !selectedWithdrawalRequest) {
      setSelectedWithdrawalRequestId('');
      setWithdrawalRejectReason('');
    }
  }, [selectedWithdrawalRequestId, selectedWithdrawalRequest]);

  useEffect(() => {
    if (!selectedClub?.id || !selectedWithdrawalRequest) {
      return undefined;
    }

    if (selectedWithdrawalRequest.status !== 'PROCESSING') {
      return undefined;
    }

    const timer = window.setInterval(() => {
      void Promise.all([loadOverviewData(), loadEntries()]);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [
    loadEntries,
    loadOverviewData,
    selectedClub?.id,
    selectedWithdrawalRequest,
  ]);

  useEffect(() => {
    if (selectedDepositIntentId && !selectedDepositIntent) {
      setSelectedDepositIntentId('');
    }
  }, [selectedDepositIntentId, selectedDepositIntent]);

  useEffect(() => {
    const hasModalOpen = Boolean(selectedDepositIntent || selectedWithdrawalRequest || selectedEntry);
    if (!hasModalOpen) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedEntryId('');
        setSelectedDepositIntentId('');
        setSelectedWithdrawalRequestId('');
        setWithdrawalRejectReason('');
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedDepositIntent, selectedEntry, selectedWithdrawalRequest]);

  const handleSelectEntry = (entry: ClubFinanceEntry) => {
    setSelectedEntryId(entry.id);
    setSuccessMessage('');
    setSettlementAmount(String(entry.outstanding_amount_reais.toFixed(2)));
    setSettlementMethod('PIX');
    setSettlementNote('');
  };

  const handleSelectWithdrawalRequest = (request: ClubWithdrawalRequest) => {
    setSelectedWithdrawalRequestId(request.id);
    setWithdrawalRejectReason(request.failure_reason || '');
    setSuccessMessage('');
  };

  const handleSelectDepositIntent = (intent: ClubPaymentIntent) => {
    setSelectedDepositIntentId(intent.id);
    setSuccessMessage('');
  };

  const handleSubmitSettlement = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedClub?.id || !selectedEntry) {
      return;
    }

    const numericAmount = Number.parseFloat(settlementAmount.replace(',', '.'));
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setError('Informe um valor valido para a baixa financeira');
      return;
    }

    setSubmittingSettlement(true);
    setError('');
    setSuccessMessage('');

    try {
      const updatedEntry = await clubService.settleFinanceEntry(selectedClub.id, selectedEntry.id, {
        amount: numericAmount,
        settlementMethod,
        note: settlementNote.trim() || undefined,
      });

      await Promise.all([loadOverviewData(), loadEntries()]);
      setSelectedEntryId(updatedEntry.id);
      setSuccessMessage('Baixa financeira registrada com sucesso');
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao registrar baixa financeira');
    } finally {
      setSubmittingSettlement(false);
    }
  };

  const handleSubmitManualEntry = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedClub?.id) {
      return;
    }

    if (!manualTargetUserId) {
      setError('Selecione um jogador para o lancamento manual');
      return;
    }

    const numericAmount = Number.parseFloat(manualAmount.replace(',', '.'));
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setError('Informe um valor valido para o lancamento manual');
      return;
    }

    const normalizedReason = manualReason.trim();
    if (!normalizedReason) {
      setError('Informe o motivo do lancamento manual');
      return;
    }

    setSubmittingManualEntry(true);
    setError('');
    setSuccessMessage('');

    try {
      const createdEntry = await clubService.createManualFinanceEntry(selectedClub.id, {
        targetUserId: manualTargetUserId,
        direction: manualDirection,
        amount: numericAmount,
        reason: normalizedReason,
      });

      await Promise.all([loadOverviewData(), loadEntries()]);
      setManualAmount('');
      setManualReason('');
      setSelectedEntryId(createdEntry.id);
      setSuccessMessage('Lancamento manual registrado com sucesso');
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao registrar lancamento manual');
    } finally {
      setSubmittingManualEntry(false);
    }
  };

  const handleApproveWithdrawal = async () => {
    if (!selectedClub?.id || !selectedWithdrawalRequest) {
      return;
    }

    setSubmittingWithdrawalAction(true);
    setError('');
    setSuccessMessage('');

    try {
      const updatedRequest = await clubService.approveWithdrawalRequest(
        selectedClub.id,
        selectedWithdrawalRequest.id
      );

      await Promise.all([loadOverviewData(), loadEntries()]);
      setSelectedWithdrawalRequestId(updatedRequest.id);
      setWithdrawalRejectReason('');
      if (updatedRequest.status === 'PROCESSING') {
        setSuccessMessage('Aprovacao enviada. Aguardando retorno final da transferencia externa.');
      } else if (updatedRequest.status === 'PAID') {
        setSuccessMessage('Solicitacao de saque aprovada e paga com sucesso');
      } else if (updatedRequest.status === 'FAILED' || updatedRequest.status === 'CANCELLED') {
        setError(
          updatedRequest.failure_reason ||
            'A transferencia externa retornou falha. Revise o detalhe da solicitacao.'
        );
      } else {
        setSuccessMessage('Solicitacao de saque aprovada com sucesso');
      }
    } catch (nextError: any) {
      setError(formatWithdrawalApproveError(nextError));
    } finally {
      setSubmittingWithdrawalAction(false);
    }
  };

  const handleRejectWithdrawal = async () => {
    if (!selectedClub?.id || !selectedWithdrawalRequest) {
      return;
    }

    const normalizedReason = withdrawalRejectReason.trim();
    if (!normalizedReason) {
      setError('Informe o motivo da recusa do saque');
      return;
    }

    setSubmittingWithdrawalAction(true);
    setError('');
    setSuccessMessage('');

    try {
      const updatedRequest = await clubService.rejectWithdrawalRequest(
        selectedClub.id,
        selectedWithdrawalRequest.id,
        normalizedReason
      );

      await Promise.all([loadOverviewData(), loadEntries()]);
      setSelectedWithdrawalRequestId(updatedRequest.id);
      setSuccessMessage('Solicitacao de saque recusada e reserva liberada');
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao recusar solicitacao de saque');
    } finally {
      setSubmittingWithdrawalAction(false);
    }
  };

  const handleResetFilters = () => {
    setSelectedPlayerId('');
    setEntryStatusFilter('PENDING');
    setEntryDirectionFilter('ALL');
    setFromDate('');
    setToDate('');
    setSuccessMessage('');
  };

  const handleExportPlayerPositions = () => {
    const directionSuffix =
      entryDirectionFilter === 'ALL' ? 'geral' : entryDirectionFilter === 'RECEIVABLE' ? 'a-receber' : 'a-pagar';
    const filename = `${toSlug(selectedClub?.name)}-financeiro-posicoes-${directionSuffix}-${buildTimestampSuffix()}.csv`;
    const headers = [
      'Jogador',
      'Papel',
      'Carteira',
      'Saldo vivo',
      'A receber',
      'A pagar',
      'Exposicao',
      'Ultima atividade financeira',
      'Ultima atividade de jogo',
    ];
    const rows = filteredPlayers.map((player) => [
      player.user_name,
      getMemberRoleLabel(player.role),
      player.wallet_balance.toFixed(2),
      player.live_result.toFixed(2),
      player.open_receivable.toFixed(2),
      player.open_payable.toFixed(2),
      player.net_exposure.toFixed(2),
      formatDateTime(player.last_financial_activity_at),
      formatDateTime(player.last_game_activity_at),
    ]);

    downloadCsv(filename, headers, rows);
    setSuccessMessage('CSV de posicoes financeiras exportado com sucesso');
  };

  const handleExportLedger = () => {
    const directionSuffix =
      entryDirectionFilter === 'ALL' ? 'geral' : entryDirectionFilter === 'RECEIVABLE' ? 'a-receber' : 'a-pagar';
    const filename = `${toSlug(selectedClub?.name)}-financeiro-ledger-${directionSuffix}-${buildTimestampSuffix()}.csv`;
    const headers = [
      'Data',
      'Jogador',
      'Direcao',
      'Valor',
      'Valor quitado',
      'Valor em aberto',
      'Status',
      'Origem',
      'Motivo',
      'Criado por',
      'Baixas registradas',
    ];
    const rows = filteredEntries.map((entry) => [
      formatDateTime(entry.created_at),
      entry.user_name,
      getDirectionLabel(entry.direction),
      entry.amount_reais.toFixed(2),
      entry.settled_amount_reais.toFixed(2),
      entry.outstanding_amount_reais.toFixed(2),
      getStatusLabel(entry.status),
      getSourceLabel(entry.source_type),
      entry.reason || '',
      entry.created_by_name || '',
      entry.settlements
        .map(
          (settlement) =>
            `${formatDateTime(settlement.created_at)} | ${settlement.settlement_method} | ${settlement.amount_reais.toFixed(2)}`
        )
        .join(' || '),
    ]);

    downloadCsv(filename, headers, rows);
    setSuccessMessage('CSV do ledger financeiro exportado com sucesso');
  };

  return (
    <div className="portal-page-grid">
      <section className="portal-metrics-grid portal-finance-metrics-grid">
        <MetricCard
          label="Rake consolidado"
          value={formatCurrency(summary?.total_rake || 0)}
          tone="positive"
          variant="dashboard"
          theme="rake"
          badge="Rake"
        />
        <MetricCard
          label="Carteiras dos jogadores"
          value={formatCurrency(summary?.wallet_balances_total || paymentSummary?.wallet_balances_total || 0)}
          helper="Saldo operacional total refletido nas carteiras do club"
          variant="dashboard"
          theme="balance"
          badge="Carteiras"
        />
        <MetricCard
          label="Depositos externos liquidos"
          value={formatCurrency(summary?.external_deposit_net_total || paymentSummary?.deposit_net_total || 0)}
          helper={`${paymentSummary?.confirmed_deposits || summary?.external_deposit_entries || 0} deposito(s) confirmados`}
          tone="positive"
          variant="dashboard"
          theme="deposit"
          badge="Gateway"
        />
        <MetricCard
          label="Custos Asaas"
          value={formatCurrency(totalGatewayFees)}
          helper={`Deposito: ${formatCurrency(summary?.external_deposit_fee_total || paymentSummary?.deposit_fee_total || 0)} . Saque: ${formatCurrency(summary?.external_withdrawal_fee_total || paymentSummary?.withdrawal_fee_total || 0)}`}
          variant="dashboard"
          theme="fee"
          badge="Custo"
        />
        <MetricCard
          label="A receber"
          value={formatCurrency(summary?.open_receivables || 0)}
          helper="Jogadores com saldo pendente para o club"
          variant="dashboard"
          theme="receivable"
          badge="Aberto"
        />
        <MetricCard
          label="A pagar"
          value={formatCurrency(summary?.open_payables || 0)}
          helper="Saidas ainda nao baixadas"
          variant="dashboard"
          theme="payable"
          badge="Aberto"
        />
        <MetricCard
          label="Saldo vivo positivo"
          value={formatCurrency(summary?.live_positive_total || 0)}
          helper="Somatorio atual de jogadores em credito nas mesas"
          variant="dashboard"
          theme="balance"
          badge="Mesas"
        />
        <MetricCard
          label="Saques em analise"
          value={String(pendingWithdrawalRequests.length)}
          helper={`${withdrawalReviewCount} em revisao . ${withdrawalProcessingCount} em processamento`}
          tone="warning"
          variant="dashboard"
          theme="warning"
          badge="Fila"
        />
      </section>

      {error ? <div className="portal-banner portal-banner--error">{error}</div> : null}
      {successMessage ? <div className="portal-banner portal-banner--success">{successMessage}</div> : null}
      {loading ? <div className="portal-loading-screen">Carregando financeiro...</div> : null}

      <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-deposit">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Operacoes externas</p>
            <h3>Depositos conciliados e custos Asaas</h3>
            <p className="portal-muted">
              Depositos externos confirmados aparecem aqui com bruto, liquido e taxa. O ledger abaixo
              segue focado em pendencias operacionais do club.
            </p>
          </div>
          <div className="portal-section-card__header-actions">
            <span className="portal-chip">Webhook: {formatDateTime(paymentSummary?.last_webhook_at)}</span>
            <span className="portal-chip">
              Confirmados: {paymentSummary?.confirmed_deposits || summary?.external_deposit_entries || 0}
            </span>
            <span className="portal-chip">
              Liquido: {formatCurrency(summary?.external_deposit_net_total || paymentSummary?.deposit_net_total || 0)}
            </span>
          </div>
        </div>

        {paymentSummary?.last_error_message ? (
          <div className="portal-banner portal-banner--warning">
            Ultimo alerta da conta externa: {paymentSummary.last_error_message}
          </div>
        ) : null}

        <div className="portal-table-wrap">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Solicitado em</th>
                <th>Jogador</th>
                <th>Bruto</th>
                <th>Liquido</th>
                <th>Taxa</th>
                <th>Status</th>
                <th>Referencia</th>
                <th>Acao</th>
              </tr>
            </thead>
            <tbody>
              {depositIntents.length > 0 ? (
                depositIntents.map((intent) => {
                  const snapshot = getDepositSnapshot(intent);
                  return (
                    <tr
                      key={intent.id}
                      className={intent.id === selectedDepositIntentId ? 'portal-table__row--active' : ''}
                    >
                      <td>{formatDateTime(intent.requested_at)}</td>
                      <td>
                        <div className="portal-table__cell-stack">
                          <strong>{playerNameByUserId[intent.user_id] || intent.user_id}</strong>
                          <span>{intent.user_id}</span>
                        </div>
                      </td>
                      <td>{formatCurrency(snapshot.gross)}</td>
                      <td>{formatCurrency(snapshot.net)}</td>
                      <td>{formatCurrency(snapshot.fee)}</td>
                      <td>
                        <span className={`portal-status-pill portal-status-pill--${getDepositStatusTone(intent.status)}`}>
                          {getDepositStatusLabel(intent.status)}
                        </span>
                      </td>
                      <td>{intent.external_reference || '-'}</td>
                      <td>
                        <button
                          type="button"
                          className="portal-secondary-button portal-secondary-button--compact"
                          onClick={() => handleSelectDepositIntent(intent)}
                        >
                          {intent.id === selectedDepositIntentId ? 'Selecionado' : 'Detalhar'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8}>Nenhum deposito externo encontrado para este club.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </section>

      <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-withdrawal">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Saque externo</p>
            <h3>Fila de aprovacao do club</h3>
          </div>
          <div className="portal-section-card__header-actions">
            <span className="portal-chip">{withdrawalRequests.length} solicitacao(oes)</span>
            <span className="portal-chip">Pendentes: {pendingWithdrawalRequests.length}</span>
          </div>
        </div>

        <div className="portal-table-wrap">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Solicitado em</th>
                <th>Solicitante / titular</th>
                <th>Valor</th>
                <th>Status</th>
                <th>Destino</th>
                <th>Reserva</th>
                <th>Acao</th>
              </tr>
            </thead>
            <tbody>
              {withdrawalRequests.length > 0 ? (
                withdrawalRequests.map((request) => (
                  <tr
                    key={request.id}
                    className={request.id === selectedWithdrawalRequestId ? 'portal-table__row--active' : ''}
                  >
                    <td>{formatDateTime(request.requested_at)}</td>
                    <td>
                      <div className="portal-table__cell-stack">
                        <strong>{playerNameByUserId[request.user_id] || request.user_id}</strong>
                        <span>{request.recipient_name_snapshot || 'Titular nao informado'}</span>
                      </div>
                    </td>
                    <td>{formatCurrency(request.amount_reais)}</td>
                    <td>
                      <span className={`portal-status-pill portal-status-pill--${getWithdrawalStatusTone(request.status)}`}>
                        {getWithdrawalStatusLabel(request.status)}
                      </span>
                    </td>
                    <td>{request.pix_key_snapshot || '-'}</td>
                    <td>{request.reservation_status || '-'}</td>
                    <td>
                      <button
                        type="button"
                        className="portal-secondary-button portal-secondary-button--compact"
                        onClick={() => handleSelectWithdrawalRequest(request)}
                      >
                        {request.id === selectedWithdrawalRequestId ? 'Selecionado' : 'Analisar'}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7}>Nenhuma solicitacao de saque encontrada para este club.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </section>

      <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-balance">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Posicao por jogador</p>
            <h3>Credores, devedores e saldo vivo</h3>
          </div>
          <div className="portal-section-card__header-actions">
            <div className="portal-inline-stats">
              <div>
                <dt>Devendo</dt>
                <dd>{summary?.players_owing_count || 0}</dd>
              </div>
              <div>
                <dt>Em credito</dt>
                <dd>{summary?.players_in_credit_count || 0}</dd>
              </div>
              <div>
                <dt>A pagar</dt>
                <dd>{summary?.players_creditor_count || 0}</dd>
              </div>
            </div>
            <span className="portal-chip">
              {directionScopeLabel}: {formatCurrency(filteredDirectionTotal)}
            </span>
            <button
              type="button"
              className="portal-secondary-button portal-secondary-button--compact"
              onClick={handleExportPlayerPositions}
              disabled={filteredPlayers.length === 0}
            >
              Exportar CSV
            </button>
          </div>
        </div>

        <div className="portal-table-wrap">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Jogador</th>
                <th>Papel</th>
                <th>Carteira</th>
                <th>Saldo vivo</th>
                <th>A receber</th>
                <th>A pagar</th>
                <th>Exposicao</th>
                <th>Acao</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlayers.length > 0 ? (
                filteredPlayers.map((player) => (
                  <tr key={player.user_id}>
                    <td>{player.user_name}</td>
                    <td>{getMemberRoleLabel(player.role)}</td>
                    <td>{formatCurrency(player.wallet_balance)}</td>
                    <td>{formatCurrency(player.live_result)}</td>
                    <td>{formatCurrency(player.open_receivable)}</td>
                    <td>{formatCurrency(player.open_payable)}</td>
                    <td>{formatCurrency(player.net_exposure)}</td>
                    <td>
                      <Link className="portal-link-button" to={`/financeiro/jogadores/${player.user_id}`}>
                        Ver trilha
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8}>Nenhuma posicao financeira encontrada para os membros do club.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-manual">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Lancamento manual</p>
            <h3>Registrar credito ou debito operacional</h3>
          </div>
          <span className="portal-chip">Nao altera a carteira do jogador</span>
        </div>

        <form className="portal-form-grid" onSubmit={handleSubmitManualEntry}>
          <label>
            <span>Jogador</span>
            <select
              value={manualTargetUserId}
              onChange={(event) => setManualTargetUserId(event.target.value)}
              disabled={submittingManualEntry}
            >
              <option value="">Selecione um jogador</option>
              {players.map((player) => (
                <option key={player.user_id} value={player.user_id}>
                  {player.user_name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Direcao</span>
            <select
              value={manualDirection}
              onChange={(event) =>
                setManualDirection(event.target.value as ClubFinanceManualEntryRequest['direction'])
              }
              disabled={submittingManualEntry}
            >
              {MANUAL_ENTRY_DIRECTION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Valor</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0.01"
              value={manualAmount}
              onChange={(event) => setManualAmount(event.target.value)}
              disabled={submittingManualEntry}
            />
          </label>

          <label className="portal-form-grid__full">
            <span>Motivo</span>
            <input
              type="text"
              value={manualReason}
              onChange={(event) => setManualReason(event.target.value)}
              placeholder="Ex.: jogador saiu devendo a diferenca do acerto"
              maxLength={255}
              disabled={submittingManualEntry}
            />
          </label>

          <div className="portal-form-grid__actions portal-form-grid__full portal-card-actions">
            <span className="portal-muted">Use este fluxo para registrar dividas ou creditos fora do contador.</span>
            <div className="portal-card-actions portal-card-actions--end">
              <button
                type="button"
                className="portal-secondary-button"
                onClick={() => {
                  setManualTargetUserId('');
                  setManualDirection('RECEIVABLE');
                  setManualAmount('');
                  setManualReason('');
                }}
                disabled={submittingManualEntry}
              >
                Limpar
              </button>
              <button type="submit" className="portal-primary-button" disabled={submittingManualEntry}>
                {submittingManualEntry ? 'Registrando...' : 'Registrar lancamento'}
              </button>
            </div>
          </div>
        </form>
      </section>

      <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-ledger">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Ledger financeiro</p>
            <h3>Lancamentos recentes do club</h3>
          </div>
          <button
            type="button"
            className="portal-secondary-button portal-secondary-button--compact"
            onClick={handleExportLedger}
            disabled={filteredEntries.length === 0}
          >
            Exportar CSV
          </button>
        </div>

        <div className="portal-finance-toolbar portal-finance-toolbar--stacked">
          <div className="portal-pill-group" role="group" aria-label="Direcao financeira">
            {ENTRY_DIRECTION_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`portal-pill-button ${entryDirectionFilter === option.value ? 'portal-pill-button--active' : ''}`}
                onClick={() => setEntryDirectionFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="portal-form-grid">
            <label>
              <span>Jogador</span>
              <select value={selectedPlayerId} onChange={(event) => setSelectedPlayerId(event.target.value)}>
                <option value="">Todos os jogadores</option>
                {players.map((player) => (
                  <option key={player.user_id} value={player.user_id}>
                    {player.user_name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Status</span>
              <select
                value={entryStatusFilter}
                onChange={(event) => setEntryStatusFilter(event.target.value as EntryStatusFilter)}
              >
                {ENTRY_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Data inicial</span>
              <input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
            </label>

            <label>
              <span>Data final</span>
              <input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
            </label>
          </div>

          <div className="portal-card-actions">
            <span className="portal-muted">
              {filteredEntries.length} lancamento(s) listado(s) em {directionScopeLabel.toLowerCase()}
            </span>
            <button type="button" className="portal-secondary-button" onClick={handleResetFilters}>
              Limpar filtros
            </button>
          </div>
        </div>

        <div className="portal-table-wrap">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Jogador</th>
                <th>Direcao</th>
                <th>Valor</th>
                <th>Em aberto</th>
                <th>Status</th>
                <th>Origem</th>
                <th>Acao</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.length > 0 ? (
                filteredEntries.map((entry) => {
                  const canSettle = entry.status === 'OPEN' || entry.status === 'PARTIAL';
                  return (
                    <tr key={entry.id} className={entry.id === selectedEntryId ? 'portal-table__row--active' : ''}>
                      <td>{formatDateTime(entry.created_at)}</td>
                      <td>{entry.user_name}</td>
                      <td>{getDirectionLabel(entry.direction)}</td>
                      <td>{formatCurrency(entry.amount_reais)}</td>
                      <td>{formatCurrency(entry.outstanding_amount_reais)}</td>
                      <td>{getStatusLabel(entry.status)}</td>
                      <td>{getSourceLabel(entry.source_type)}</td>
                      <td>
                        {canSettle ? (
                          <button
                            type="button"
                            className="portal-secondary-button portal-secondary-button--compact"
                            onClick={() => handleSelectEntry(entry)}
                          >
                            {entry.id === selectedEntryId ? 'Abrir baixa' : 'Dar baixa'}
                          </button>
                        ) : (
                          <span className="portal-muted">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8}>Nenhum lancamento financeiro encontrado para o filtro atual.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </section>

      {selectedDepositIntent ? (
        <FinanceModal
          eyebrow="Operacao externa"
          title={playerNameByUserId[selectedDepositIntent.user_id] || selectedDepositIntent.user_id}
          subtitle={`Solicitado em ${formatDateTime(selectedDepositIntent.requested_at)}`}
          onClose={() => setSelectedDepositIntentId('')}
        >
          {(() => {
            const snapshot = getDepositSnapshot(selectedDepositIntent);
            return (
              <>
                <div className="portal-inline-stats">
                  <div>
                    <dt>Bruto</dt>
                    <dd>{formatCurrency(snapshot.gross)}</dd>
                  </div>
                  <div>
                    <dt>Liquido</dt>
                    <dd>{formatCurrency(snapshot.net)}</dd>
                  </div>
                  <div>
                    <dt>Taxa</dt>
                    <dd>{formatCurrency(snapshot.fee)}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>{getDepositStatusLabel(selectedDepositIntent.status)}</dd>
                  </div>
                </div>

                <div className="portal-form-grid">
                  <label>
                    <span>Referencia externa</span>
                    <input value={selectedDepositIntent.external_reference || '-'} readOnly />
                  </label>

                  <label>
                    <span>Pagamento Asaas</span>
                    <input value={selectedDepositIntent.provider_payment_id || '-'} readOnly />
                  </label>

                  <label>
                    <span>Status do provedor</span>
                    <input value={snapshot.providerStatus} readOnly />
                  </label>

                  <label>
                    <span>Finance entry</span>
                    <input value={selectedDepositIntent.finance_entry_id || '-'} readOnly />
                  </label>

                  <label>
                    <span>Confirmado em</span>
                    <input value={formatDateTime(selectedDepositIntent.confirmed_at)} readOnly />
                  </label>

                  <label>
                    <span>Expira em</span>
                    <input value={formatDateTime(selectedDepositIntent.expires_at)} readOnly />
                  </label>

                  <label className="portal-form-grid__full">
                    <span>Descricao</span>
                    <input value={selectedDepositIntent.description || 'Sem descricao'} readOnly />
                  </label>

                  <div className="portal-form-grid__actions portal-form-grid__full portal-card-actions">
                    <span className="portal-muted">
                      O club recebe aqui o bruto, o liquido efetivo e o custo do gateway para tratar
                      a operacao como custo financeiro.
                    </span>
                    <div className="portal-card-actions portal-card-actions--end">
                      {selectedDepositIntent.provider_invoice_url ? (
                        <a
                          className="portal-link-button"
                          href={selectedDepositIntent.provider_invoice_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Abrir no Asaas
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </FinanceModal>
      ) : null}

      {selectedEntry ? (
        <FinanceModal
          eyebrow="Baixa financeira"
          title={selectedEntry.user_name}
          subtitle={`${getDirectionLabel(selectedEntry.direction)} . Em aberto ${formatCurrency(selectedEntry.outstanding_amount_reais)}`}
          onClose={() => setSelectedEntryId('')}
        >
          <div className="portal-inline-stats">
            <div>
              <dt>Direcao</dt>
              <dd>{getDirectionLabel(selectedEntry.direction)}</dd>
            </div>
            <div>
              <dt>Em aberto</dt>
              <dd>{formatCurrency(selectedEntry.outstanding_amount_reais)}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{getStatusLabel(selectedEntry.status)}</dd>
            </div>
            <div>
              <dt>Origem</dt>
              <dd>{getSourceLabel(selectedEntry.source_type)}</dd>
            </div>
          </div>

          <form className="portal-form-grid" onSubmit={handleSubmitSettlement}>
            <label>
              <span>Valor da baixa</span>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0.01"
                max={selectedEntry.outstanding_amount_reais}
                value={settlementAmount}
                onChange={(event) => setSettlementAmount(event.target.value)}
                disabled={submittingSettlement}
              />
            </label>

            <label>
              <span>Metodo</span>
              <select
                value={settlementMethod}
                onChange={(event) => setSettlementMethod(event.target.value as typeof settlementMethod)}
                disabled={submittingSettlement}
              >
                {SETTLEMENT_METHODS.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="portal-form-grid__full">
              <span>Observacao</span>
              <input
                type="text"
                value={settlementNote}
                onChange={(event) => setSettlementNote(event.target.value)}
                placeholder="Opcional"
                maxLength={255}
                disabled={submittingSettlement}
              />
            </label>

            <div className="portal-form-grid__actions portal-form-grid__full portal-card-actions">
              <button
                type="button"
                className="portal-secondary-button"
                onClick={() => setSettlementAmount(String(selectedEntry.outstanding_amount_reais.toFixed(2)))}
                disabled={submittingSettlement}
              >
                Usar valor total
              </button>
              <button type="submit" className="portal-primary-button" disabled={submittingSettlement}>
                {submittingSettlement ? 'Registrando...' : 'Confirmar baixa'}
              </button>
            </div>
          </form>

          <div className="portal-settlement-panel">
            <div>
              <p className="portal-section-card__eyebrow">Historico de baixas</p>
              <h3>Quitacoes registradas</h3>
            </div>
            {selectedEntry.settlements.length > 0 ? (
              <div className="portal-feed">
                {selectedEntry.settlements.map((settlement) => (
                  <div key={settlement.id} className="portal-feed__item">
                    <div>
                      <strong>{formatCurrency(settlement.amount_reais)}</strong>
                      <p>
                        {settlement.settlement_method} | {settlement.settled_by_name || 'Operador'}
                      </p>
                    </div>
                    <div>
                      <p>{formatDateTime(settlement.created_at)}</p>
                      <p className="portal-muted">{settlement.note || 'Sem observacao'}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="portal-muted">Nenhuma baixa registrada ainda para este lancamento.</p>
            )}
          </div>
        </FinanceModal>
      ) : null}

      {selectedWithdrawalRequest ? (
        <FinanceModal
          eyebrow="Saque externo"
          title={selectedWithdrawalRequest.recipient_name_snapshot || 'Saque externo'}
          subtitle={`Solicitante: ${playerNameByUserId[selectedWithdrawalRequest.user_id] || selectedWithdrawalRequest.user_id}`}
          onClose={() => {
            setSelectedWithdrawalRequestId('');
            setWithdrawalRejectReason('');
          }}
        >
          <div className="portal-inline-stats">
            <div>
              <dt>Valor</dt>
              <dd>{formatCurrency(selectedWithdrawalRequest.amount_reais)}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{getWithdrawalStatusLabel(selectedWithdrawalRequest.status)}</dd>
            </div>
            <div>
              <dt>Reserva</dt>
              <dd>{selectedWithdrawalRequest.reservation_status || '-'}</dd>
            </div>
            <div>
              <dt>Taxa</dt>
              <dd>
                {formatCurrency(
                  toSnapshotMoney(selectedWithdrawalRequest.metadata_json?.transfer_snapshot?.transfer_fee, 0)
                )}
              </dd>
            </div>
          </div>

          <div className="portal-form-grid">
            <label>
              <span>Destino Pix</span>
              <input value={selectedWithdrawalRequest.pix_key_snapshot || '-'} readOnly />
            </label>

            <label>
              <span>Documento do titular</span>
              <input value={selectedWithdrawalRequest.recipient_document_snapshot || '-'} readOnly />
            </label>

            <label>
              <span>Referencia externa</span>
              <input value={selectedWithdrawalRequest.external_reference || '-'} readOnly />
            </label>

            <label>
              <span>Transferencia Asaas</span>
              <input value={selectedWithdrawalRequest.provider_transfer_id || '-'} readOnly />
            </label>

            <label className="portal-form-grid__full">
              <span>Motivo informado pelo jogador</span>
              <input value={selectedWithdrawalRequest.reason || 'Sem observacao'} readOnly />
            </label>

            <label className="portal-form-grid__full">
              <span>Motivo da recusa</span>
              <input
                type="text"
                value={withdrawalRejectReason}
                onChange={(event) => setWithdrawalRejectReason(event.target.value)}
                placeholder="Obrigatorio ao recusar"
                disabled={
                  submittingWithdrawalAction ||
                  !['REQUESTED', 'UNDER_REVIEW', 'RESERVED'].includes(selectedWithdrawalRequest.status)
                }
              />
            </label>

            <div className="portal-form-grid__actions portal-form-grid__full portal-card-actions">
              <span className="portal-muted">
                Aprovacao envia a transferencia externa. Recusa encerra o pedido e libera a reserva.
              </span>
              <div className="portal-card-actions portal-card-actions--end">
                <button
                  type="button"
                  className="portal-secondary-button"
                  onClick={() => setWithdrawalRejectReason('')}
                  disabled={submittingWithdrawalAction}
                >
                  Limpar motivo
                </button>
                <button
                  type="button"
                  className="portal-secondary-button"
                  onClick={() => void handleRejectWithdrawal()}
                  disabled={
                    submittingWithdrawalAction ||
                    !['REQUESTED', 'UNDER_REVIEW', 'RESERVED'].includes(selectedWithdrawalRequest.status)
                  }
                >
                  {submittingWithdrawalAction ? 'Processando...' : 'Recusar saque'}
                </button>
                <button
                  type="button"
                  className="portal-primary-button"
                  onClick={() => void handleApproveWithdrawal()}
                  disabled={
                    submittingWithdrawalAction ||
                    !['REQUESTED', 'UNDER_REVIEW', 'RESERVED'].includes(selectedWithdrawalRequest.status)
                  }
                >
                  {submittingWithdrawalAction ? 'Processando...' : 'Aprovar saque'}
                </button>
              </div>
            </div>

            {selectedWithdrawalRequest.failure_reason ? (
              <div className="portal-banner portal-banner--warning portal-form-grid__full">
                Falha registrada: {selectedWithdrawalRequest.failure_reason}
              </div>
            ) : null}
          </div>
        </FinanceModal>
      ) : null}
    </div>
  );
};

export default FinancePage;
